import { map } from 'lodash';

import {
  ACTION_INPUT_TYPES,
  FIELD_KEYS,
  INPUT_FIELD_TYPE_LABELS,
} from 'app/developer-v3/constants';

import type {
  ActionInputType,
  FormFieldTemplate,
} from 'app/developer-v3/types';

export const getFormFields = (
  type: ActionInputType = ACTION_INPUT_TYPES.INPUT,
  disallowedKeys: string[] = []
): FormFieldTemplate[] => {
  if (type === ACTION_INPUT_TYPES.INPUT) {
    return [
      {
        type: 'single-line-string',
        key: FIELD_KEYS.key,
        label: 'Key',
        required: true,
        schemaKey: true,
        help_text_html:
          '<p>Enter a unique identifier for this input field using snake_case (lowercase with underscores). Match your API’s field name if possible. Used internally in Zapier. Example: <code>ticket_name</code>.</p>',
        disallowed: disallowedKeys,
        placeholder: 'ticket_name',
      },
      {
        type: 'single-line-string',
        key: FIELD_KEYS.label,
        label: 'Label',
        required: true,
        help_text_html:
          '<p>Enter a user-friendly name that describes the expected input. Capitalize each word. Displayed to users in the Zapier UI. Example: <code>Ticket Name</code>.</>',
        placeholder: 'Ticket Name',
      },
      {
        type: 'string',
        rows: 5,
        key: FIELD_KEYS.helpText,
        label: 'Help Text',
        min: 20,
        help_text_html:
          '<p>Provide a more detailed explanation of what users need to enter. Use Markdown to format text and link to help docs on your site where useful. Displayed to users in the Zapier UI. Example: <code>Enter the ticket name</code>. <a href="https://docs.zapier.com/platform/build/add-fields#add-an-input-field-to-a-trigger-or-action" target="_blank">Learn more</a>.</p>',
        placeholder: 'Enter the ticket name',
      },
      {
        type: 'pretty-select',
        label: 'Type',
        help_text_html: '<p>What type of data does your API expect?</p>',
        key: FIELD_KEYS.type,
        choices: map(INPUT_FIELD_TYPE_LABELS, (label, value) => ({
          value,
          label,
        })),
        default: 'string',
        hasSearch: false,
      },
      {
        type: 'single-line-string',
        key: FIELD_KEYS.default,
        label: 'Default Text',
        help_text_html:
          '<p>Enter a default value that makes sense if the user leaves this field blank. Use only if you’re confident it’s what most users would expect. <a href="https://docs.zapier.com/platform/build/add-fields#add-an-input-field-to-a-trigger-or-action" target="_blank">Learn more</a>.</p>',
      },
      {
        type: 'fields',
        key: FIELD_KEYS.options,
        label: 'Options',
        fields: [
          {
            type: 'boolean-input',
            key: FIELD_KEYS.required,
            label: 'Required',
          },
          {
            type: 'boolean-input',
            key: FIELD_KEYS.list,
            label: 'Allows Multiples',
          },
          {
            type: 'boolean-input',
            key: FIELD_KEYS.altersDynamicFields,
            label: 'Alters Dynamic Fields',
          },
        ],
      },
    ];
  }

  if (type === ACTION_INPUT_TYPES.GROUP) {
    return [
      {
        type: 'single-line-string',
        key: FIELD_KEYS.key,
        label: 'Line Item Group Key',
        required: true,
        schemaKey: true,
        help_text_html:
          '<p>Enter a unique identifier for this line item group using snake_case (lowercase with underscores). Example: <code>new_tickets</code>.</p>',
        placeholder: 'new_tickets',
      },
      {
        type: 'single-line-string',
        key: FIELD_KEYS.label,
        label: 'Line Item Group Label',
        required: false,
        help_text_html:
          '<p>Enter a clear and descriptive name for this line item group to indicate what the group is. Capitalize each word. This label will be visible to users in Zapier. Example: <code>New Tickets</code>.</>',
        placeholder: 'New Tickets',
      },
    ];
  }

  return [];
};
